import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './SearchSelectAthlete.module.css';
import SelectIcon from 'assets/images/select.png';
import { ReactComponent as DeleteIcon } from 'assets/images/icon_delete.svg';
import { formatGroupCell } from 'utils/groupLabel';

const SearchSelectAthlete = ({
  columnKey,
  row,
  options = [],
  showSelect,
  handleShowSelect,
  handleOptionChange,
  handleDeleteIconClick,
  groupingBasis,
}) => {
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    setSearchText(row?.athleteName || '');
  }, [row?.athleteName]);

  const filteredOptions = options.filter((option) => {
    if (!showSelect) return false;
    const search = searchText.trim().toLowerCase();
    if (!search) return true;
    // If search is all digits, match clubAthleteNumber startsWith
    if (/^\d+$/.test(search)) {
      return option.clubAthleteNumber?.toString().startsWith(search);
    }
    // Otherwise, match name
    const name = `${option.firstName} ${option.lastName}`.toLowerCase();
    return name.includes(search);
  });

  const renderOptionLabel = (option) => {
    // e.g. 569 - John Chase . M . 12, or . M . Y6 for a club that groups by year level
    return `${option.clubAthleteNumber} - ${option.firstName} ${option.lastName} • ${option.gender?.[0] || ''} • ${formatGroupCell(groupingBasis, option.ageGroup)}`;
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    setSearchText(newValue);

    if (newValue === '') {
      handleShowSelect(columnKey, false);
      // Only call handleOptionChange when clearing the field completely
      handleOptionChange('');
    } else {
      handleShowSelect(columnKey, true);
      // Don't call handleOptionChange while typing - only when selecting
    }
  };

  const handleSelect = (option) => {
    //console.log('SearchSelectAthlete - handleSelect called with:', option);

    const fullName = `${option.firstName} ${option.lastName}`;

    //console.log('SearchSelectAthlete - calling handleOptionChange with:', option);
    handleOptionChange(option); // Pass full athlete object instead of just name

    setSearchText(fullName);
    handleShowSelect(columnKey, false);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <div className={styles.inputOptionWrapper}>
        <input
          type="text"
          className={styles.overlaidInput}
          value={searchText}
          onChange={handleChange}
          placeholder=""
        />

        {showSelect && (
          <div
            style={{
              position: 'absolute',
              top: '35px',
              border: '1px solid #e1edf3',
              borderRadius: '4px',
              width: '280px',
              maxWidth: '280px',
              minWidth: '280px',
              maxHeight: '200px',
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '6px',
              background: '#fff',
              boxShadow: '#00000029 0 0 12px',
              zIndex: 1000,
            }}
          >
            {filteredOptions.map((option, index) => (
              <div
                key={index}
                style={{
                  lineHeight: '1.4',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  padding: '6px 4px',
                  fontSize: '13px',
                  marginBottom: '2px',
                  borderRadius: '2px',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
                onClick={() => handleSelect(option)}
              >
                {renderOptionLabel(option)}
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div
                style={{ padding: '6px 4px', fontSize: '12px', color: '#666' }}
              >
                No Athletes found
              </div>
            )}
          </div>
        )}
        <img
          src={SelectIcon}
          alt="select"
          onClick={() => handleShowSelect(columnKey, !showSelect)}
        />
      </div>
      <div className={styles.deleteIcon} onClick={handleDeleteIconClick}>
        <DeleteIcon />
      </div>
    </div>
  );
};

SearchSelectAthlete.propTypes = {
  options: PropTypes.arrayOf(PropTypes.any),
  handleOptionChange: PropTypes.func,
  showSelect: PropTypes.bool,
  handleShowSelect: PropTypes.func,
  columnKey: PropTypes.string,
  handleDeleteIconClick: PropTypes.func,
  row: PropTypes.object,
  // How the club groups its athletes (utils/groupLabel.js); an age club when not given.
  groupingBasis: PropTypes.number,
};

export default SearchSelectAthlete;
